/**
 * Dark-pill banner shown at the top of HomeScreen / CartScreen when the
 * guest is in an active group cart. Tapping opens GroupSheet.
 *
 * Renders avatar stack + "Live shared cart · N people" label + a chevron.
 */
import { useTranslation } from "react-i18next";

import type { GroupCartParticipant } from "@/api/group-cart";
import { AvatarStack } from "./AvatarStack";

export interface GroupBannerProps {
  participants: GroupCartParticipant[];
  onTap: () => void;
}

export function GroupBanner({ participants, onTap }: GroupBannerProps) {
  const { t } = useTranslation();
  return (
    <button
      data-testid="group-banner"
      onClick={onTap}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "calc(100% - 32px)",
        margin: "12px 16px 0",
        padding: "10px 12px 10px 12px",
        background: "var(--ink-1)",
        color: "#fff",
        border: "none",
        borderRadius: 14,
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left",
        boxShadow: "0 6px 20px -8px rgba(0,0,0,0.3)",
      }}
      aria-label={t("v2.group_cart.view_group")}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: "var(--primary-400, #34d399)",
          boxShadow: "0 0 0 4px rgba(52, 211, 153, 0.18)",
          flexShrink: 0,
        }}
      />
      <AvatarStack participants={participants} max={3} size={26} dark />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700 }}>
          {t("v2.group_cart.active_label")}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.7)",
            marginTop: 1,
          }}
        >
          {t("v2.group_cart.people_count", {
            count: participants.length,
            defaultValue: `${participants.length} people`,
          })}
        </div>
      </div>
      <span
        aria-hidden
        style={{ fontSize: 18, color: "rgba(255,255,255,0.7)", flexShrink: 0 }}
      >
        ›
      </span>
    </button>
  );
}
